import { Maybe } from '@csp/csp-common-model';
import { RoleType } from '@csp/dmdp-api-common-dto';
import { ConsentActionOutcomeV1 } from '@csp/dmdp-api-consent-dto';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { toConfigFromMetaV1 } from '../../mapper/ConsentRequestMetaMapper';
import { ArtifactType } from '../../model/ArtifactType';
import {
  ConsentActionOutcomeV1WithArtifactAndScheduleMeta,
  ConsentActionWithArtifactMeta,
} from '../../model/ConsentAction';
import { ConsentArtifactConfigV2 } from '../../model/ConsentArtifact';
import { ConsentFeatureType } from '../../model/ConsentConfigOnFeatureV1';
import { ConsentPlatform } from '../../model/ConsentPlatform';
import { ConsentRequestConfigMeta } from '../../model/ConsentRequestConfigMeta';
import { matchesPlatform } from '../../util/consentPlatformUtils';
import { LegacyConsentService } from '../LegacyConsentService';

export const mapToConsentActionOutcomeWithMeta = (
  outcome: ConsentActionOutcomeV1,
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
  roles: RoleType[],
  onFeatures: Maybe<Array<ConsentFeatureType>>,
  consentTypesToFilterOut: Maybe<Array<string>>,
): ConsentActionOutcomeV1WithArtifactAndScheduleMeta | undefined => {
  if (isTextConsent(outcome) || isLegacyConsentOfMatchingType(outcome, platform, legacyConsentTypes)) {
    const artifactConfig = fromOutcomeToConsentArtifactConfigV2(outcome, platform);
    const scheduleMeta = calculateScheduleMeta(outcome, roles, platform);

    if (!scheduleMeta || !artifactConfig) {
      return undefined;
    }

    const consentOnFeature = scheduleMeta.onFeature;
    // We count onFeatures = [] as no onFeatures filtering, in order to be consistent with how getPendingConsents interprets onFeatures = []
    const isConsentOnFeatures =
      consentOnFeature && onFeatures && onFeatures.length > 0
        ? consentOnFeature.some(feature => onFeatures.includes(feature.type))
        : true;

    const isConsentTypeToFilterOut =
      consentTypesToFilterOut?.some((consentType: string) => consentType === artifactConfig.cmsConsentType) ?? false;
    const hasRolesMatch = scheduleMeta.roles.some(role => roles.includes(role));

    return hasRolesMatch && isConsentOnFeatures && !isConsentTypeToFilterOut
      ? { ...outcome, artifactMeta: { CONFIG_V2: artifactConfig }, scheduleMeta }
      : undefined;
  } else {
    return undefined;
  }
};

const isTextConsent = (outcome: ConsentActionOutcomeV1): boolean =>
  outcome.artifactType === ArtifactType.TEXT && isConfig_V2Artifact(outcome);

const isLegacyConsentOfMatchingType = (
  outcome: ConsentActionOutcomeV1,
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
): boolean =>
  isLegacyArtifact(outcome) &&
  LegacyConsentService.hasMatchingClientTypeAndConsentType(outcome, platform, legacyConsentTypes);

const fromOutcomeToConsentArtifactConfigV2 = (
  outcome: ConsentActionOutcomeV1 | ConsentActionWithArtifactMeta,
  platform: ConsentPlatform,
): ConsentArtifactConfigV2 | undefined => {
  const CONFIG_V2 = outcome.artifactMeta?.CONFIG_V2;

  const consentArtifactConfig = CONFIG_V2
    ? {
        version: CONFIG_V2.version,
        cmsConsentType: CONFIG_V2.cmsConsentType,
        country: CONFIG_V2.country,
        clientType: CONFIG_V2.clientType ?? platform,
      }
    : isConsentActionOutcomeV1(outcome)
      ? {
          version: outcome.scheduleMeta?.version,
          cmsConsentType: outcome.artifactType,
          country: outcome.artifactMeta?.country,
          clientType: outcome.artifactTags?.find((artifactTag: string) => artifactTag === platform),
        }
      : null;

  return consentArtifactConfig?.version &&
    consentArtifactConfig?.clientType &&
    consentArtifactConfig?.cmsConsentType &&
    consentArtifactConfig?.country
    ? consentArtifactConfig
    : undefined;
};

const isConsentActionOutcomeV1 = (
  outcome: ConsentActionOutcomeV1 | ConsentActionWithArtifactMeta,
): outcome is ConsentActionOutcomeV1 => (outcome as ConsentActionOutcomeV1).artifactMeta !== undefined;

const isConfig_V2Artifact = (outcome: ConsentActionOutcomeV1): boolean => {
  const CONFIG_V2 = outcome.artifactMeta?.CONFIG_V2;

  return Boolean(CONFIG_V2?.version && CONFIG_V2?.cmsConsentType && CONFIG_V2?.country);
};

const calculateScheduleMeta = (
  outcome: ConsentActionOutcomeV1,
  roles: RoleType[],
  platform: ConsentPlatform,
): ConsentRequestConfigMeta | null => {
  if (isLegacyArtifact(outcome)) {
    return {
      roles,
      platform,
      onFeature: [{ type: ConsentFeatureType.ON_ONBOARDING }],
      mandatory: true,
      dataProcessing: false,
      enableReconsent: true,
    };
  } else if (outcome.scheduleMeta) {
    const scheduleMeta = toConfigFromMetaV1(outcome.scheduleMeta);
    const platforms = Array.isArray(scheduleMeta.platform) ? scheduleMeta.platform : [scheduleMeta.platform];

    return matchesPlatform(platforms, platform) ? scheduleMeta : null;
  } else {
    return null;
  }
};

const isLegacyArtifact = (outcome: ConsentActionOutcomeV1): boolean =>
  outcome.artifactTags && outcome.scheduleMeta?.version && outcome.artifactType && outcome.artifactMeta?.country;

export const ExportsForTestTextConsentMetaConfig = {
  isTextConsent,
  isLegacyConsentOfMatchingType,
  fromOutcomeToConsentArtifactConfigV2,
  isConsentActionOutcomeV1,
  isConfig_V2Artifact,
  calculateScheduleMeta,
  isLegacyArtifact,
};
